import { useAuth } from "@elit-hair/elit-tools";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@elit-hair/elit-ui";
import { createFileRoute } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { TbPlus, TbRefresh, TbX } from "react-icons/tb";
import { toast } from "sonner";

import { PaginationControls } from "@/components/ui/pagination-controls";
import { SearchBar } from "@/components/ui/search-bar";
import { getTotalPages, PAGINATION } from "@/constants/pagination";
import { PERMISSIONS } from "@/constants/permissions";
import { useApiKeys, useDeleteApiKey } from "@/hooks/queries/api-key.queries";
import { ApiKeyCard } from "@/pages/api-keys/components/api-key-card";
import { ApiKeyCreatedDialog } from "@/pages/api-keys/components/api-key-created-dialog";
import { ApiKeyFormDialog } from "@/pages/api-keys/components/api-key-form-dialog";
import { ApiKeysTable } from "@/pages/api-keys/components/api-keys-table";
import { API_KEYS_PAGE_SIZE } from "@/pages/api-keys/constants";
import { ApiKeysSearchParamsSchema } from "@/pages/api-keys/types/api-keys-search.types";
import {
  extractApiMessage,
  filterApiKeys,
} from "@/pages/api-keys/utils/api-keys.utils";
import { ApiKey, CreatedApiKey } from "@/types/api-key.types";
import { APP_ROUTES_FULL, APP_ROUTES_TO } from "@/utils/routes";

type FormDialogState = "create" | ApiKey | null;

export const Route = createFileRoute(APP_ROUTES_FULL.API_KEYS)({
  component: ApiKeysPage,
  validateSearch: zodValidator(ApiKeysSearchParamsSchema),
  staticData: {
    config: {
      breadcrumb: { title: "api_keys", to: APP_ROUTES_TO.API_KEYS },
      title: "api_keys",
      layout: { scroll: false },
    },
    permissions: [PERMISSIONS.AUTH_SVC_API_KEYS_VIEW],
  },
});

function ApiKeysPage() {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const searchParams = Route.useSearch({ structuralSharing: true });
  const { userData } = useAuth();
  const permissionCodes = userData?.permissionCodes || [];

  const canManage = permissionCodes.includes(
    PERMISSIONS.AUTH_SVC_API_KEYS_MANAGE
  );

  const page = searchParams.page ?? PAGINATION.DEFAULT_PAGE;
  const searchQuery = searchParams.search?.trim() ?? "";

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [formDialog, setFormDialog] = useState<FormDialogState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);

  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  const handleDebouncedSearch = useCallback(
    (value: string) => {
      navigate({
        to: Route.to,
        search: (old) => ({
          ...old,
          search: value || undefined,
          page: undefined,
        }),
        replace: true,
      });
    },
    [navigate]
  );

  const handlePageChange = (nextPage: number) => {
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        page: nextPage === PAGINATION.DEFAULT_PAGE ? undefined : nextPage,
      }),
      replace: true,
    });
  };

  const clearFilters = () => {
    setSearchInput("");
    navigate({
      to: Route.to,
      search: (old) => ({
        ...old,
        search: undefined,
        page: undefined,
      }),
      replace: true,
    });
  };

  const { data, isLoading, isFetching, isError, error, refetch } = useApiKeys();
  const deleteMutation = useDeleteApiKey();

  const filteredKeys = useMemo(
    () => filterApiKeys(data ?? [], searchQuery),
    [data, searchQuery]
  );

  const total = filteredKeys.length;
  const totalPages = Math.max(1, getTotalPages(total, API_KEYS_PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const paginatedKeys = filteredKeys.slice(
    (safePage - 1) * API_KEYS_PAGE_SIZE,
    safePage * API_KEYS_PAGE_SIZE
  );

  const hasActiveFilters = searchQuery.length > 0;
  const isRefreshing = isFetching && !!data;

  useEffect(() => {
    if (page > totalPages) {
      handlePageChange(totalPages);
    }
  }, [page, totalPages]);

  const handleCreated = (created: CreatedApiKey) => {
    setFormDialog(null);
    setCreatedKey(created);
    handlePageChange(PAGINATION.DEFAULT_PAGE);
    toast.success(t("api_key_created"));
  };

  const handleUpdated = () => {
    setFormDialog(null);
    toast.success(t("api_key_updated"));
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteMutation.mutateAsync(deleteTarget.id);
      toast.success(t("api_key_deleted"));
      setDeleteTarget(null);
    } catch (err) {
      toast.error(extractApiMessage(err, t("failed_to_delete_api_key")));
    }
  };

  if (isError) {
    return (
      <ErrorState
        title={t("failed_to_load_api_keys")}
        message={extractApiMessage(error, t("try_again_later"))}
        onRetry={() => refetch()}
      />
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="flex shrink-0 flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("api_keys")}</h1>
          <p className="text-muted-foreground">
            {isLoading && !data
              ? t("api_keys_page_description")
              : t("api_keys_count", { count: total })}
          </p>
        </div>

        <div className="flex w-full flex-wrap gap-x-2 gap-y-3 sm:w-auto">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                onClick={() => refetch()}
                disabled={isRefreshing}
              >
                {isRefreshing ? (
                  <Spinner size="sm" className="md:mr-2" />
                ) : (
                  <Icon icon={TbRefresh} className="md:mr-2" />
                )}
                <span className="hidden md:inline">{t("refresh")}</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t("refresh")}</TooltipContent>
          </Tooltip>

          {canManage && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="md:mr-2" />
                  <span className="hidden md:inline">{t("add_api_key")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent>{t("add_api_key")}</TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <div className="flex shrink-0 flex-wrap items-center gap-3">
        <SearchBar
          value={searchInput}
          onChange={setSearchInput}
          onDebouncedChange={handleDebouncedSearch}
          placeholder={t("search_api_keys")}
          className="sm:w-72"
        />

        {hasActiveFilters && (
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            <Icon icon={TbX} className="mr-1.5" />
            {t("clear_filters")}
          </Button>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        {!isLoading && paginatedKeys.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-4">
            <EmptyState
              variant="simple"
              title={
                hasActiveFilters ? t("no_matches") : t("no_api_keys_found")
              }
              description={
                hasActiveFilters
                  ? t("try_different_search")
                  : t("create_your_first_api_key")
              }
            />
            {hasActiveFilters ? (
              <Button variant="outline" onClick={clearFilters}>
                <Icon icon={TbX} className="mr-2" />
                {t("clear_filters")}
              </Button>
            ) : (
              canManage && (
                <Button onClick={() => setFormDialog("create")}>
                  <Icon icon={TbPlus} className="mr-2" />
                  {t("add_api_key")}
                </Button>
              )
            )}
          </div>
        ) : (
          <>
            <div className="min-h-0 flex-1 overflow-hidden">
              <div className="hidden h-full md:block">
                <ApiKeysTable
                  apiKeys={paginatedKeys}
                  isLoading={isLoading && !data}
                  canManage={canManage}
                  onEdit={setFormDialog}
                  onDelete={setDeleteTarget}
                />
              </div>
              <div className="h-full space-y-3 overflow-auto md:hidden">
                {isLoading && !data
                  ? Array.from({ length: 4 }).map((_, index) => (
                      <div
                        key={index}
                        className="h-36 animate-pulse rounded-xl border border-border bg-muted/40"
                      />
                    ))
                  : paginatedKeys.map((apiKey) => (
                      <ApiKeyCard
                        key={apiKey.id}
                        apiKey={apiKey}
                        canManage={canManage}
                        onEdit={setFormDialog}
                        onDelete={setDeleteTarget}
                      />
                    ))}
              </div>
            </div>

            {totalPages > 1 && (
              <div className="flex shrink-0 items-center justify-between gap-3 border-t border-border bg-muted/30 px-1 py-2 sm:px-2">
                <p className="text-xs text-muted-foreground">
                  {t("showing_results", {
                    start: (safePage - 1) * API_KEYS_PAGE_SIZE + 1,
                    end: Math.min(safePage * API_KEYS_PAGE_SIZE, total),
                    total,
                  })}
                </p>
                <PaginationControls
                  currentPage={safePage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                  isLoading={isFetching}
                />
              </div>
            )}
          </>
        )}
      </div>

      <ApiKeyFormDialog
        open={formDialog !== null}
        apiKey={formDialog === "create" ? null : formDialog}
        onClose={() => setFormDialog(null)}
        onCreated={handleCreated}
        onUpdated={handleUpdated}
      />

      <ApiKeyCreatedDialog
        open={createdKey !== null}
        apiKey={createdKey}
        onClose={() => setCreatedKey(null)}
      />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("delete_api_key")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("delete_api_key_confirmation", {
                name: deleteTarget?.name,
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>
              {t("cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending ? t("deleting") : t("delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
